import type { Currency } from '@/api/types/public';
import type { CommonCurrencyInfo } from '@/utils/currency/util-currency';
import { httpRequest } from '@/utils/axios';
import clientLogger from '@/utils/client-logger';
import Cookie from 'js-cookie';
import { getDefaultCurrencyFromList, getEnvDefaultCurrency } from '@/utils/currency/util-currency';

export type { CommonCurrencyInfo } from '@/utils/currency/util-currency';

function getCurrencyCodeFromUrlSearch(search: string): string {
  const qs = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(qs);
  const raw = params.get('currency') || params.get('app_currency_code');
  if (!raw) return '';
  return raw.trim().toUpperCase();
}

function parseZlCurrencyCookieRaw(zlCurrencyCookie: string | undefined | null): string {
  if (!zlCurrencyCookie) return '';
  try {
    const parsed = JSON.parse(zlCurrencyCookie) as Currency | string;
    if (typeof parsed === 'object' && parsed && parsed.name) {
      return parsed.name;
    }
    return typeof parsed === 'string' ? parsed : zlCurrencyCookie;
  } catch {
    return zlCurrencyCookie;
  }
}

/**
 * 用 `typeof document` 区分环境；可在 RSC / Server Action / 浏览器中调用。
 *
 * - 无 `document`：只返回站点环境变量默认（`currencyItems` 由 layout 等传入列表时优先用列表解析）。
 * - 有 `document`：`currencyItems` 有数据则不再请求接口；否则拉列表后按 URL → Cookie → IP → 环境变量默认。
 */
export async function getCurrency(currencyItems?: Currency[]): Promise<CommonCurrencyInfo> {
  // * 如果是服务端
  if (typeof document === 'undefined') {
    return {
      currency: getEnvDefaultCurrency(currencyItems ?? []),
      source: 'Env',
    };
  }
  // * 如果是客户端
  let currencyList: Currency[] = currencyItems?.length ? [...currencyItems] : [];
  if (currencyList.length === 0) {
    try {
      const res = (await httpRequest('GET:/api/public/currency')) as { data?: Currency[] };
      if (Array.isArray(res?.data)) {
        currencyList = res.data;
      }
    } catch (error) {
      clientLogger.error('Failed to fetch currency list on client:', error);
    }
  }

  if (currencyList.length === 0) {
    return { currency: getEnvDefaultCurrency([]), source: 'Env' };
  }

  const codeFromUrl = getCurrencyCodeFromUrlSearch(document.location.search);
  if (codeFromUrl) {
    const currency = currencyList.find((item) => item.name === codeFromUrl);
    if (currency) {
      return { currency, source: 'URL' };
    }
  }

  const cookieName = parseZlCurrencyCookieRaw(Cookie.get('zl-currency'));
  if (cookieName) {
    const currency = currencyList.find((item) => item.name === cookieName);
    if (currency) {
      return { currency, source: 'Cookie' };
    }
  }

  const ipCurrency = getDefaultCurrencyFromList(currencyList);
  if (ipCurrency) {
    return { currency: ipCurrency, source: 'IP' };
  }

  return { currency: getEnvDefaultCurrency(currencyList), source: 'Env' };
}
